import { useSubscription } from '@apollo/client';
import React, { useEffect } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { Button, Icon, Skeleton, Text, useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';

import { DetailsPageSupportedOutputTypes } from '../AppointmentDetailsOutput';
import { GeneratingOutput } from '../GeneratingOutput';

export const NO_OUTPUT_ID = 'no-output-available';

const outputSubscription = graphql(`
  subscription getAppointmentOutput(
    $appointmentId: uuid!
    $outputType: LlmOutputTypesEnum!
  ) {
    appointmentLlmOutputs(
      where: {
        appointmentId: { _eq: $appointmentId }
        output: {
          modelPromptTemplate: {
            template: { outputType: { _eq: $outputType } }
          }
        }
      }
      limit: 1
      orderBy: { output: { startedAt: DESC } }
    ) {
      __typename
      llmOutputId
      output {
        __typename
        id
        status
        content
      }
    }
  }
`);

export interface NoOutputAvailableProps {
  tenantId: string;
  appointmentId: string;
  outputType: DetailsPageSupportedOutputTypes;
  refetch: () => Promise<any>;
}

export const NoOutputAvailable: React.FC<NoOutputAvailableProps> = ({
  tenantId,
  appointmentId,
  outputType,
  refetch,
}) => {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const toast = useToast();

  const generateOutput = async () => {
    try {
      const result = await apiClient.llm.generateAppointmentOutput({
        params: { tenantId, appointmentId },
        body: { outputType },
      });

      if (result.status === 422) {
        toast.error(result.body.message);
        return;
      }

      if (result.status !== 201) {
        toast.error(t('appointmentTools.outputs.generateOutputFailed'));
        return;
      }
    } catch (e) {
      toast.error(t('appointmentTools.outputs.generateOutputFailed'));
    }
  };

  const { data, loading } = useSubscription(outputSubscription, {
    variables: {
      appointmentId,
      outputType,
    },
  });

  useEffect(() => {
    if (data?.appointmentLlmOutputs?.[0]?.output?.content) {
      refetch();
    }
  }, [refetch, data]);

  const llmOutput = data?.appointmentLlmOutputs?.[0];

  if (loading || !data) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
        <Skeleton className="h-4 w-[200px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  if (data?.appointmentLlmOutputs?.length === 0) {
    return (
      <div className="border border-dashed py-4 text-center">
        <Text weight="semibold" className="mt-2">
          {t('appointmentTools.outputs.noOutputAvailable')}
        </Text>
        <Text color="secondary" className="mt-1"></Text>
        <div className="mt-2">
          <Button onClick={generateOutput}>
            <Icon
              name="Plus"
              size="lg"
              className="mr-1.5 -ml-0.5"
              aria-hidden="true"
            />
            {t('appointment.insights.generate')}
          </Button>
        </div>
      </div>
    );
  }

  if (llmOutput && !llmOutput.output.content) {
    return <GeneratingOutput />;
  }
};
